/**
 * TopicChipsSection Component
 * 
 * Two-row horizontal scrolling topic chips for Explore
 */

import React from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme } from '../../../components/ui';

export interface TopicChip {
  id: string;
  name: string;
  icon: string;
}

interface TopicChipsSectionProps {
  title?: string;
  subtitle?: string;
  topics: TopicChip[];
  onTopicPress: (topic: TopicChip) => void;
}

// Color palette for category icons
const CATEGORY_COLORS = [
  '#007D9B', // Primary teal
  '#FF7F4F', // Coral/orange
  '#8B5CF6', // Purple
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

// Get consistent color for a category based on its ID
const getCategoryColor = (categoryId: string): string => {
  const hash = categoryId.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return CATEGORY_COLORS[Math.abs(hash) % CATEGORY_COLORS.length];
};

export const TopicChipsSection: React.FC<TopicChipsSectionProps> = ({
  title = 'Explore Topics',
  subtitle,
  topics,
  onTopicPress,
}) => {
  // Split topics into two rows
  const midpoint = Math.ceil(topics.length / 2);
  const firstRow = topics.slice(0, midpoint);
  const secondRow = topics.slice(midpoint);

  const renderChip = (topic: TopicChip) => {
    const iconColor = getCategoryColor(topic.id);
    
    return (
      <Pressable
        key={topic.id}
        style={({ pressed }) => [
          styles.chip,
          pressed && styles.chipPressed,
        ]}
        onPress={() => onTopicPress(topic)}
      >
        <Ionicons
          name={topic.icon as any}
          size={18}
          color={iconColor}
        />
        <Text variant="body2" style={styles.chipText}>
          {topic.name}
        </Text>
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text variant="h3" style={styles.title}>{title}</Text>
        {subtitle && (
          <Text variant="caption" style={styles.subtitle}>
            {subtitle}
          </Text>
        )}
      </View>

      {/* First Row */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {firstRow.map(renderChip)}
      </ScrollView>

      {/* Second Row */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {secondRow.map(renderChip)}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 40,
  },
  header: {
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
  },
  title: {
    color: 'rgb(0, 71, 89)',
  },
  subtitle: {
    marginTop: 2,
    color: 'rgba(0, 71, 89, 0.7)',
  },
  row: {
    paddingHorizontal: Theme.spacing.lg,
    gap: Theme.spacing.sm,
    paddingBottom: Theme.spacing.xs,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
    backgroundColor: Theme.colors.surface,
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
    borderRadius: 999, // Fully rounded corners
    ...Theme.shadows.sm,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  chipPressed: {
    opacity: 0.7,
    transform: [{ scale: 0.98 }],
  },
  chipText: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: 'rgb(0, 71, 89)',
  },
});

